/**
 * RecoveryWizardTab — Wave 6.3 SuperAdmin restore-from-snapshot surface.
 *
 * Filters: date range, actor, entity type, reason code, has-test-mode,
 * customer-name-text-search. Each result row: file path + JSON preview
 * (collapsible) + actor + reason + "Restore this record" button. Bulk
 * restore for batch correction reversals.
 *
 * PILOT-GATE — surfaces records for non-pilot officers in pilot phase only.
 */

import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '@retail-ops/api-client';
import { Banner, Button, Pill } from '@/components/primitives';
import { promptBypassKey } from '@/modules/Admin/editors/_shared/CrudTable';
import { useRecordsApi } from '../hooks/useRecordsApi';
import type {
  Iso8601,
  PurgeEntityType,
  PurgeReasonCode,
  RecordsRecoveryFilters,
  RecordsRecoveryRow,
} from '@retail-ops/shared-types';
import { TestModeBadge } from '@/modules/_shared/TestModeBadge';

const ENTITY_TYPES: ReadonlyArray<{ value: PurgeEntityType | 'all'; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'Cart', label: 'Carts' },
  { value: 'CartProduct', label: 'Cart products' },
  { value: 'MaintAction', label: 'Maintenance' },
];

export function RecoveryWizardTab(): JSX.Element {
  const { client, makeSignal } = useRecordsApi();
  const [rows, setRows] = useState<ReadonlyArray<RecordsRecoveryRow>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<ReadonlyArray<PurgeReasonCode>>([]);
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [bulkSubmitting, setBulkSubmitting] = useState<boolean>(false);
  const [submittedToast, setSubmittedToast] = useState<string | null>(null);

  // Filters
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [actorOfficerCode, setActorOfficerCode] = useState<string>('');
  const [entityFilter, setEntityFilter] = useState<PurgeEntityType | 'all'>('all');
  const [reasonFilter, setReasonFilter] = useState<string>('');
  const [hasTestMode, setHasTestMode] = useState<'any' | 'true' | 'false'>('any');
  const [nameQuery, setNameQuery] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    client.retail.config.purgeReasonCodes({ signal: makeSignal() }).then(
      (res) => {
        if (cancelled) return;
        const anyRes = res as unknown as { success?: boolean; data?: PurgeReasonCode[] };
        if (anyRes.success !== false) setReasons(anyRes.data ?? []);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function search(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const f: RecordsRecoveryFilters = {};
      if (startDate) f.startDate = new Date(startDate).toISOString() as Iso8601;
      if (endDate) f.endDate = new Date(endDate).toISOString() as Iso8601;
      if (actorOfficerCode) f.actorOfficerCode = actorOfficerCode;
      if (entityFilter !== 'all') f.entityType = entityFilter as PurgeEntityType;
      if (reasonFilter) f.reasonCode = reasonFilter;
      if (hasTestMode !== 'any') f.hasTestMode = hasTestMode === 'true';
      if (nameQuery.trim()) f.customerNameQuery = nameQuery.trim();
      const res = await client.retail.superAdmin.records.recovery(f, { signal: makeSignal() });
      const anyRes = res as unknown as {
        success?: boolean;
        data?: RecordsRecoveryRow[];
        error?: string;
      };
      if (anyRes.success === false) {
        setError(anyRes.error ?? 'Failed to search recovery snapshots.');
        return;
      }
      setRows(anyRes.data ?? []);
      setSelected(new Set());
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : 'Failed to search recovery snapshots.');
    } finally {
      setLoading(false);
    }
  }

  async function restoreOne(row: RecordsRecoveryRow): Promise<void> {
    const bypass = promptBypassKey();
    if (!bypass) return;
    try {
      const res = await client.retail.superAdmin.records.recoverFromSnapshot(
        bypass,
        { purgeHistoryId: row.purgeHistoryId },
        { signal: makeSignal() },
      );
      if (!res.success) {
        setError(res.error ?? 'Restore failed.');
        return;
      }
      setSubmittedToast(`Restored ${res.restoredEntityType ?? row.entityType}.`);
      void search();
    } catch (e: unknown) {
      if (ApiError.isApiError(e)) setError(e.message);
      else if (e instanceof Error) setError(e.message);
      else setError('Unexpected error during restore.');
    }
  }

  async function bulkRestore(): Promise<void> {
    if (selected.size === 0 || bulkSubmitting) return;
    const bypass = promptBypassKey();
    if (!bypass) return;
    setBulkSubmitting(true);
    setError(null);
    try {
      let restored = 0;
      for (const id of selected) {
        const res = await client.retail.superAdmin.records.recoverFromSnapshot(
          bypass,
          { purgeHistoryId: id },
          { signal: makeSignal() },
        );
        if (res.success) restored++;
      }
      setSubmittedToast(`Restored ${restored} of ${selected.size} record${selected.size === 1 ? '' : 's'}.`);
      void search();
    } catch (e: unknown) {
      if (e instanceof Error) setError(e.message);
    } finally {
      setBulkSubmitting(false);
    }
  }

  function toggleRow(id: number): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const visibleCount = useMemo(() => rows.length, [rows]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <Banner tone="info">
        Recovery restores a record to its pre-purge SQL state from the JSON snapshot in{' '}
        <code>ao_PurgeRecords</code>. This re-creates the row with the original primary key —
        any subsequent CIF/account references that pointed at the original row will resolve
        again automatically.
      </Banner>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 'var(--sp-4)',
          padding: 'var(--sp-5)',
          background: 'var(--surface-card)',
          border: '1px solid var(--surface-border)',
          borderRadius: 'var(--r-md)',
        }}
      >
        <Filter label="Purged from">
          <input aria-label="Start date" title="Start date" type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
        </Filter>
        <Filter label="Purged to">
          <input aria-label="End date" title="End date" type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
        </Filter>
        <Filter label="Actor (officer code)">
          <input aria-label="Actor officer code" title="Actor officer code" type="text" value={actorOfficerCode} onChange={(e) => setActorOfficerCode(e.target.value)} placeholder="Any actor" style={inputStyle} />
        </Filter>
        <Filter label="Entity type">
          <select aria-label="Entity type" title="Entity type" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value as PurgeEntityType | 'all')} style={inputStyle}>
            {ENTITY_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Filter>
        <Filter label="Reason code">
          <select aria-label="Reason code" title="Reason code" value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)} style={inputStyle}>
            <option value="">Any reason</option>
            {reasons.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </Filter>
        <Filter label="Test mode">
          <select aria-label="Test-mode filter" title="Test-mode filter" value={hasTestMode} onChange={(e) => setHasTestMode(e.target.value as 'any' | 'true' | 'false')} style={inputStyle}>
            <option value="any">Any</option>
            <option value="true">Test only</option>
            <option value="false">Production only</option>
          </select>
        </Filter>
        <Filter label="Customer name">
          <input aria-label="Customer name search" title="Customer name search" type="text" value={nameQuery} onChange={(e) => setNameQuery(e.target.value)} placeholder="Substring" style={inputStyle} />
        </Filter>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button variant="primary" size="sm" onClick={() => void search()}>
            Search snapshots
          </Button>
        </div>
      </div>

      {submittedToast && <Banner tone="ok">{submittedToast}</Banner>}
      {error && <Banner tone="err">{error}</Banner>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>
          {loading ? 'Searching…' : `${visibleCount} snapshot${visibleCount === 1 ? '' : 's'} found`}
          {selected.size > 0 && (
            <span style={{ marginLeft: 'var(--sp-3)', color: 'var(--pcb-blue)', fontWeight: 600 }}>
              {selected.size} selected
            </span>
          )}
        </div>
        <Button variant="primary" size="sm" disabled={selected.size === 0 || bulkSubmitting} onClick={() => void bulkRestore()}>
          {bulkSubmitting ? 'Restoring…' : `Restore ${selected.size} selected`}
        </Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        {rows.map((row) => (
          <RecoveryCard
            key={row.purgeHistoryId}
            row={row}
            selected={selected.has(row.purgeHistoryId)}
            onToggle={() => toggleRow(row.purgeHistoryId)}
            onRestore={() => void restoreOne(row)}
          />
        ))}
      </div>
    </div>
  );
}

interface RecoveryCardProps {
  row: RecordsRecoveryRow;
  selected: boolean;
  onToggle: () => void;
  onRestore: () => void;
}

function RecoveryCard({ row, selected, onToggle, onRestore }: RecoveryCardProps): JSX.Element {
  const [expanded, setExpanded] = useState<boolean>(false);
  const summary = row.snapshotSummary;
  return (
    <div
      style={{
        background: 'var(--surface-card)',
        border: `1px solid ${selected ? 'var(--pcb-blue)' : 'var(--surface-border)'}`,
        borderRadius: 'var(--r-md)',
        padding: 'var(--sp-5)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
        <input
          aria-label={`Select snapshot ${row.entityKeyHumanized ?? row.entityKey}`}
          title={`Select snapshot ${row.entityKeyHumanized ?? row.entityKey}`}
          type="checkbox"
          checked={selected}
          onChange={onToggle}
        />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
            <Pill variant="muted">{row.entityType}</Pill>
            <strong>{row.entityKeyHumanized ?? row.entityKey}</strong>
            {summary?.isTestMode && <TestModeBadge active size="sm" />}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 4 }}>
            Purged {formatDateTime(row.purgedAt)} by{' '}
            {row.purgedByDisplayName ?? row.purgedByOfficerCode ?? '—'} —{' '}
            {row.reasonCodeLabel ?? row.reasonCode}: {row.reason}
          </div>
        </div>
        <Button variant="primary" size="sm" onClick={onRestore}>
          Restore
        </Button>
      </div>
      <div style={{ marginTop: 'var(--sp-3)' }}>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{ background: 'transparent', border: 'none', color: 'var(--pcb-blue)', cursor: 'pointer', fontSize: 12 }}
        >
          {expanded ? '▾ Hide snapshot details' : '▸ Show snapshot details'}
        </button>
        {expanded && (
          <div style={{ marginTop: 'var(--sp-3)', padding: 'var(--sp-3)', background: 'var(--ink-100)', borderRadius: 'var(--r-md)', fontSize: 12, color: 'var(--ink-800)', fontFamily: 'monospace' }}>
            <div>Snapshot path: <code>{row.snapshotPath ?? 'n/a'}</code></div>
            {summary && (
              <div style={{ marginTop: 6 }}>
                {summary.customerName && <div>Customer: {summary.customerName}</div>}
                {summary.branch && <div>Branch: {summary.branch}</div>}
                {summary.productCodes && summary.productCodes.length > 0 && (
                  <div>Products: {summary.productCodes.join(', ')}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Filter({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--ink-600)' }}>
      <span style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
      {children}
    </label>
  );
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const inputStyle: React.CSSProperties = {
  padding: '4px 8px',
  fontSize: 12,
  border: '1px solid var(--ink-300)',
  borderRadius: 4,
  background: 'var(--surface-card)',
};
